import Loader from 'components/ui/LoadingIndicator';
import ContainerBox from 'components/ui/ContainerBox';
import { Option, Select, TextArea } from 'components/ui/FormControls';
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useFormControl } from '../../../../utils/form.utils';
import { requiredValidator } from '../../../../utils/validator.utils';
import { baseUrls } from '../../../../utils/baseUrls.utils';
import {
    ChannelSubscribedUser,
    ChannelSubscriberAddEditResponse,
    GET_PREMIUM_CHANNEL_BY_ID_PUBLISHERS_DASHBOARD,
} from '../../../../gql/channels';
import {
    BulkChannelSubscriptionsInput,
    CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY,
    CREATE_BULK_CHANNEL_SUBSCRIPTIONS,
} from '../../../../gql/subscriptions';
import {
    ChannelSubscription,
    ChannelTypes,
    User,
} from '../../../../shared/types';
import { sortArray } from '../../../../utils/sorting.utils';
import {
    CHECK_EMAILS,
    CheckEmailResponse,
    PAGINATED_USERS_SEARCH,
} from '../../../../gql/users';
import { debounce } from 'lodash';
import {
    NOTES_CHAR_LIMIT,
    subscribersMainSx,
} from './ChannelSubscribersShared';
import { ChannelSubscribersBulkAdd } from './ChannelSubscribersBulkAdd';
import { getDisplayProperties } from '../../../../shared/channelSubscription';
import { FreeChannelSubscribersForm } from './FreeChannelSubscribersForm';
import { PremiumChannelSubscribersForm } from './PremiumChannelSubscribersForm';
import ManageTemplateFormItem from '../../../ui/ManageTemplateFormItem';
import { Box, Grid } from '@mui/material';
import { LocalFile } from '../../../ui/admin/LocalFile';
import { sanitizeRouteId } from '../../../../utils/route-params.utils';

import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { ErrorText } from 'components/ui/FormControls';
export const ChannelSubscribersWrapper = (): JSX.Element => {
    const { _id: routeChannelId, neonUserId: encodedNeonUserId } = useParams<{
        _id?: string;
        neonUserId?: string;
    }>();
    const channelId = sanitizeRouteId(routeChannelId) || '';
    const neonUserId = encodedNeonUserId
        ? decodeURIComponent(encodedNeonUserId)
        : undefined;
    const isEdit = !!neonUserId;
    const history = useHistory();
    const location = useLocation();
    const isBulk = location.pathname.endsWith(
        baseUrls.publisher.premiumChannels.bulkAddSubscribers
            .split('/')
            .pop() || ''
    );

    const [bulkFormValues, setBulkFormValues] = useState<
        BulkChannelSubscriptionsInput | undefined
    >();

    const [cancelNonStripeSubscriptionImmediately] = useMutation<
        boolean,
        { channelId: string; userId: string }
    >(CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY, {
        onCompleted: () => {
            history.push(
                `${baseUrls.publisher.premiumChannels.subscribersStatsMain}/${channelId}`
            );
        },
    });

    const [createBulkChannelSubscriptions] = useMutation<
        boolean,
        {
            item: BulkChannelSubscriptionsInput;
            users: string[];
            existing: string[];
        }
    >(CREATE_BULK_CHANNEL_SUBSCRIPTIONS, {
        onCompleted: () => {
            history.push(
                `${baseUrls.publisher.premiumChannels.subscribersStatsMain}/${channelId}`
            );
        },
    });

    const { data, loading: channelLoading } =
        useQuery<ChannelSubscriberAddEditResponse>(
            GET_PREMIUM_CHANNEL_BY_ID_PUBLISHERS_DASHBOARD({
                includePreviouslySubscribedUsers: false,
            }),
            {
                skip: !channelId,
                variables: { id: channelId },
                fetchPolicy: 'network-only',
            }
        );

    const { refetch: fetchUsers } = useQuery(PAGINATED_USERS_SEARCH, {
        skip: true,
    });

    const [validateEmails, { data: emailsCheck, loading: emailsCheckLoading }] =
        useLazyQuery<
            CheckEmailResponse,
            { emails: string[]; channelId: string }
        >(CHECK_EMAILS, {
            fetchPolicy: 'network-only',
        });

    const [currentUser, setCurrentUser] = useState<
        ChannelSubscribedUser | undefined
    >();

    const userFormControl = useFormControl<Option | undefined>(undefined, [
        requiredValidator,
    ]);
    const notesFormControl = useFormControl<string>(
        '',
        isEdit ? [] : [requiredValidator]
    );
    const fileFormControl = useFormControl<File | undefined>(undefined, [
        requiredValidator,
    ]);
    const [emailCheckResults, setEmailCheckResults] =
        useState<CheckEmailResponse>();

    const returnListUrl = `${baseUrls.publisher.premiumChannels.subscribersStatsMain}/${channelId}`;

    const proceedBulk = (values: BulkChannelSubscriptionsInput) => {
        setBulkFormValues(values);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const csv = evt.target?.result || '';
            const emails = (csv as string)
                .split('\n')
                .map((line) => line.split(',')[0].trim())
                .filter((email) => email.indexOf('@') > 0);
            validateEmails({ variables: { emails, channelId } });
        };
        reader.readAsText(fileFormControl.value as any);
    };

    const addSubscriptions = (overwriteSubscribed: boolean) => {
        if (!bulkFormValues) return;
        const userIDs =
            emailCheckResults?.checkEmails.users.map((u) => u._id) || [];
        const existingUserIDs = overwriteSubscribed
            ? emailCheckResults?.checkEmails.existing.map((u) => u._id) || []
            : [];
        const variables = {
            item: bulkFormValues,
            users: userIDs,
            existing: existingUserIDs,
        };
        createBulkChannelSubscriptions({ variables }).then((r) => r);
    };

    const onDelete = async () => {
        currentUser &&
            (await cancelNonStripeSubscriptionImmediately({
                variables: {
                    channelId,
                    userId: currentUser._id,
                },
            }));
    };

    const subscribedUsersMap: { [key: string]: true } = useMemo(() => {
        if (!data?.channel.channelSubscribedUsers.length) return {};
        return data.channel.channelSubscribedUsers.reduce(
            (res, user) => ({
                ...res,
                [user._id]: !getDisplayProperties(
                    user.channelSubscriptions[0] as ChannelSubscription
                ).isTrialSubscriptionExpired,
            }),
            {}
        );
    }, [data]);

    const findUsers = useCallback(
        debounce((input: string, callback) => {
            fetchUsers({
                params: {
                    searchQuery: input,
                },
            }).then((r) => {
                const users: User[] = r?.data?.getPaginatedUsers?.users || [];
                const usersSelectOptions: Option[] = sortArray(
                    users
                        .filter((u) => !subscribedUsersMap[u._id])
                        .map((user): Option => ({
                            label: `${user.name} (${user.email})`,
                            value: user._id,
                        })),
                    'label'
                );
                callback(usersSelectOptions);
            });
        }, 500),
        [data]
    );

    useEffect(() => setEmailCheckResults(emailsCheck), [emailsCheck]);

    useEffect(() => {
        if (isEdit) {
            const currentUser = data?.channel.channelSubscribedUsers.find(
                (user) => user._id === neonUserId
            );
            setCurrentUser(currentUser);
        }
    }, [isEdit, data?.channel]);

    if (
        !data?.channel ||
        (isEdit && !currentUser) ||
        channelLoading ||
        emailsCheckLoading
    )
        return <Loader />;

    const isPremiumChannelSubscriptionType =
        data.channel.type === ChannelTypes.premium;

    if (emailCheckResults && data) {
        return (
            <ChannelSubscribersBulkAdd
                onCancel={() => {
                    setBulkFormValues(undefined);
                    setEmailCheckResults(undefined);
                }}
                onConfirm={(overwriteSubscribed) =>
                    addSubscriptions(overwriteSubscribed)
                }
                channel={data.channel}
                {...emailCheckResults.checkEmails}
            />
        );
    }

    const usersField = (
        <ManageTemplateFormItem>
            {isBulk ? (
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <LocalFile
                            accept=".csv"
                            description="Allowed file format is CSV"
                            onFileChanged={(file) => {
                                fileFormControl.setValue(file);
                            }}
                            error={fileFormControl.showError}
                        />
                    </Grid>
                    {fileFormControl.showError && (
                        <Grid size={12}>
                            <ErrorText>
                                {fileFormControl.validationMessage}
                            </ErrorText>
                        </Grid>
                    )}
                </Grid>
            ) : (
                <Select
                    id={'channel-admins-select'}
                    size="medium"
                    clearable={true}
                    loadOptions={findUsers}
                    menuPlacement="bottom"
                    placeholder="Start typing…"
                    label="Name"
                    value={userFormControl.value}
                    error={userFormControl.showError}
                    errorText={userFormControl.validationMessage}
                    onBlur={userFormControl.handleBlur}
                    onChange={(option: Option): void => {
                        userFormControl.setValue(option);
                    }}
                    helperText="The user must already have a Neon login and have signed into Insights at least once."
                />
            )}
        </ManageTemplateFormItem>
    );

    const notesField = (
        <ManageTemplateFormItem>
            <TextArea
                id={'add-subscriber-notes'}
                fullWidth
                type="text"
                label="Reason for activating this subscription on user's behalf"
                error={notesFormControl?.showError}
                errorText={notesFormControl?.validationMessage}
                autoComplete="off"
                rowsMax={4}
                value={notesFormControl?.value}
                showProgressBar={false}
                onBlur={notesFormControl?.handleBlur}
                onChange={(e: ChangeEvent<any>): void => {
                    const val: string = e?.target?.value;
                    if (val?.length > NOTES_CHAR_LIMIT) {
                        return;
                    }
                    notesFormControl?.setValue(val);
                }}
            />
        </ManageTemplateFormItem>
    );

    return (
        <ContainerBox>
            <Box sx={subscribersMainSx}>
                {isPremiumChannelSubscriptionType ? (
                    <PremiumChannelSubscribersForm
                        channel={data.channel}
                        channelId={channelId}
                        userFormControl={userFormControl}
                        notesFormControl={notesFormControl}
                        fileFormControl={fileFormControl}
                        usersField={usersField}
                        notesField={notesField}
                        returnUrl={returnListUrl}
                        isEdit={isEdit}
                        isBulk={isBulk}
                        proceedBulk={proceedBulk}
                        currentUser={isEdit ? currentUser : undefined}
                        onDelete={onDelete}
                    />
                ) : (
                    <FreeChannelSubscribersForm
                        channel={data.channel}
                        channelId={channelId}
                        userFormControl={userFormControl}
                        notesFormControl={notesFormControl}
                        fileFormControl={fileFormControl}
                        usersField={usersField}
                        notesField={notesField}
                        proceedBulk={proceedBulk}
                        returnUrl={returnListUrl}
                        isEdit={isEdit}
                        isBulk={isBulk}
                        currentUser={isEdit ? currentUser : undefined}
                        onDelete={onDelete}
                    />
                )}
            </Box>
        </ContainerBox>
    );
};
